import { ReactElement, useCallback, useContext } from "react";
import Select, { MultiValue, SingleValue } from "react-select";
import { debounce } from "lodash";
import { ThemeContext } from "styled-components";

interface SelectTagProps<T> {
  options?: T[];
  values?: T[];
  placeholder?: string;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  onChange?: (value: T[]) => void;
  onInputChange?: (value: string) => void;
}

const SelectTag = <T extends unknown>({
  options,
  values,
  placeholder = "",
  onChange,
  onInputChange,
  closeMenuOnSelect = false,
  isMulti = true,
}: SelectTagProps<T>): ReactElement | null => {
  const theme = useContext(ThemeContext);

  const handleOnChange = useCallback(
    (value: MultiValue<T> | SingleValue<T>) => {
      if (onChange) {
        onChange(Array.isArray(value) ? value.map((v) => v) : [value]);
      }
    },
    [onChange]
  );

  const _debounce = debounce((newValue: string) => {
    if (onInputChange) onInputChange(newValue);
  }, 300);

  const handleOnInputChange = useCallback(_debounce, [
    onInputChange,
    _debounce,
  ]);

  const customStyles = {
    option: () => ({
      width: "100%",
      display: "flex",
      flex: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: ".5rem",
      cursor: "pointer",
      color: theme.typography.regular.color,
      backgroundColor: "transparent",
      border: "1px solid transparent",
      margin: "0",
    }),
    menuList: (provided: any, state: any) => ({
      ...provided,
      top: "-1px",
      left: "-1px",
      width: "calc(100% + 2px)",
      margin: "0",
      padding: ".5rem",
      backgroundColor: "red",
      listStyle: "none",
      background: "#FFFFFF",
      borderRadius: "0.3125rem",
      color: theme.typography.strong.color,
      zindex: "1",
      boxShadow: "0px 15px 25px rgba(0, 0, 0, 0.15)",
    }),
    control: () => ({
      background: "#FFFFFF",
      borderRadius: "0.3125rem",
      display: "flex",
      alignItems: "center",
      border: "1px solid #D9D9D9",
      color: theme.typography.strong.color,
      "min-height": "1.8rem",
      fontSize: "0.875rem",
    }),
    multiValue: (control: any, state: any) => ({
      ...control,
      background: "#F5EDFF",
      borderRadius: "5px",
    }),
    dropdownIndicator: (control: any, state: any) => ({
      ...control,
      color: theme.typography.strong.color,
      transform: state.selectProps.menuIsOpen && "rotate(180deg)",
    }),
    clearIndicator: (control: any, state: any) => ({
      ...control,
      color: "#DDDDDD",
    }),
    multiValueRemove: (control: any, state: any) => ({
      ...control,
      "&:hover": {
        backgroundColor: "#c7a5f0",
        color: "#F5EDFF",
      },
    }),
  };

  return (
    <div>
      <Select
        closeMenuOnSelect={closeMenuOnSelect}
        isMulti={isMulti}
        value={values}
        options={options}
        styles={customStyles}
        placeholder={placeholder}
        onChange={(value) => handleOnChange(value)}
        onInputChange={(value) => handleOnInputChange(value)}
      />
    </div>
  );
};

export default SelectTag;
